/**
 * NewAPITestModal
 * Three-card picker shown when user clicks "New API Test" on the Dashboard.
 *
 * Paths:
 *   Manual        → navigate('/create-api-test')
 *   cURL Import   → inline CurlImportModal → navigate with prefilled step
 *   Collection    → navigate('/import-collection')
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Edit3, Terminal, Package, ChevronRight } from "lucide-react";
import CurlImportModal from "@/components/CurlImportModal";

export default function NewAPITestModal({ open, onClose }) {
  const navigate = useNavigate();
  const [showCurl, setShowCurl] = useState(false);

  const handleClose = () => {
    setShowCurl(false);
    onClose();
  };

  // Called when CurlImportModal successfully parses a step
  const handleCurlImport = (parsedStep) => {
    handleClose();
    navigate("/create-api-test", { state: { prefillSteps: [parsedStep] } });
  };

  const cards = [
    {
      icon: <Edit3 className="w-6 h-6 text-[#00E599]" />,
      bg: "bg-[#00E599]/10 border-[#00E599]/20",
      title: "Build Manually",
      description: "Add steps one by one. Full control over method, URL, headers, body, assertions, and variable chaining.",
      action: () => { handleClose(); navigate("/create-api-test"); },
      cta: "Start Building",
      ctaClass: "bg-[#00E599] text-black hover:bg-[#00CC88]",
    },
    {
      icon: <Terminal className="w-6 h-6 text-blue-400" />,
      bg: "bg-blue-500/10 border-blue-500/20",
      title: "Import from cURL",
      description: "Paste a cURL command. We'll extract the method, URL, headers, and body automatically to pre-fill your first step.",
      action: () => { onClose(); setShowCurl(true); },
      cta: "Paste cURL",
      ctaClass: "bg-blue-500 text-white hover:bg-blue-600",
    },
    {
      icon: <Package className="w-6 h-6 text-[#D946EF]" />,
      bg: "bg-[#D946EF]/10 border-[#D946EF]/20",
      title: "Import Collection",
      description: "Upload a Postman collection JSON. Pick endpoints and either build manually or let AI generate full test cases with assertions and variable chaining.",
      action: () => { handleClose(); navigate("/import-collection"); },
      cta: "Upload Collection",
      ctaClass: "bg-[#D946EF] text-white hover:bg-[#C026D3]",
    },
  ];

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="bg-[#0B0F1A] border-white/10 text-white max-w-3xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Unbounded, sans-serif" }}>
              New API Test
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Choose how you want to create your API test case.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            {cards.map((card) => (
              <div
                key={card.title}
                className={`glass-card rounded-xl border p-5 flex flex-col gap-4 cursor-pointer hover:opacity-90 transition-opacity ${card.bg}`}
                onClick={card.action}
              >
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${card.bg}`}>
                  {card.icon}
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-white mb-1">{card.title}</h3>
                  <p className="text-slate-400 text-xs leading-relaxed">{card.description}</p>
                </div>
                <Button
                  className={`w-full rounded-sm text-sm font-semibold ${card.ctaClass}`}
                  onClick={e => { e.stopPropagation(); card.action(); }}
                >
                  {card.cta}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* cURL import lives outside the main modal so it can open after the main closes */}
      <CurlImportModal
        open={showCurl}
        onClose={() => setShowCurl(false)}
        onImport={handleCurlImport}
      />
    </>
  );
}
